import React, { useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { CONTENT, IMAGES, UI } from "../../lib/content";
import { useLang } from "../../lib/LangContext";
import { ChapterHead, Reveal, ChapterShell } from "./Shell";
import { MonogramSeal, Wordmark } from "../Logo";
import { TID } from "../../lib/testIds";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/* ---------- XIV. STORYTELLING ---------- */
export function StorytellingChapter() {
    const { lang } = useLang();
    const c = CONTENT.storytelling[lang];
    const ref = React.useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
    return (
        <ChapterShell slug="storytelling">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XIV" />
            </div>
            <div className="col-span-12 md:col-span-7" ref={ref}>
                <motion.div style={{ y }} className="aspect-[4/5] overflow-hidden">
                    <img src={IMAGES.marettimo} alt="Marettimo" className="w-full h-full object-cover" style={{ filter: "contrast(1.08) saturate(0.5) sepia(0.15)" }} />
                </motion.div>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 mt-10 md:mt-32 self-start">
                <Reveal>
                    <p className="font-serif-lux italic text-2xl md:text-3xl leading-[1.35]">{c.body}</p>
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="mt-10 space-y-3 font-micro text-[10px]" style={{ letterSpacing: "0.32em" }}>
                        <div className="flex gap-4">
                            <span style={{ color: "#C9A96A" }}>37° 58′ N</span>
                            <span>12° 03′ E</span>
                        </div>
                        <div className="opacity-70">TRAPANI · SICILIA · ITALIA</div>
                        <div className="opacity-50">MARETTIMO · ISOLE EGADI</div>
                    </div>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- XV. EXPERIENCE / UNBOXING ---------- */
export function ExperienceChapter() {
    const { lang } = useLang();
    const c = CONTENT.experience[lang];
    return (
        <ChapterShell slug="experience">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XV" />
            </div>
            <div className="col-span-12 md:col-span-9 md:col-start-2 mt-4">
                <ol>
                    {c.steps.map((s, i) => (
                        <Reveal key={i} delay={i * 0.1}>
                            <li className="flex items-start gap-8 py-8 border-b" style={{ borderColor: "rgba(11,11,11,0.15)" }}>
                                <span className="font-didone italic text-3xl md:text-4xl w-16 shrink-0" style={{ color: "#C9A96A" }}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="font-serif-lux text-xl md:text-2xl leading-[1.4]">{s}</p>
                            </li>
                        </Reveal>
                    ))}
                </ol>
            </div>
        </ChapterShell>
    );
}

/* ---------- XVI. WEBSITE ---------- */
export function WebsiteChapter() {
    const { lang } = useLang();
    const c = CONTENT.website[lang];
    return (
        <ChapterShell slug="website">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XVI" />
            </div>
            <div className="col-span-12 md:col-span-6 mt-4">
                <Reveal>
                    <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]">{c.body}</p>
                </Reveal>
            </div>
            {/* Browser mockup */}
            <div className="col-span-12 mt-16">
                <Reveal>
                    <div className="border" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                        {/* fake browser chrome */}
                        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "rgba(201,169,106,0.35)", background: "#FBF9F4" }}>
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#0B0B0B", opacity: 0.15 }} />
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#0B0B0B", opacity: 0.15 }} />
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#0B0B0B", opacity: 0.15 }} />
                            <div className="flex-1 ml-4 text-center font-micro text-[10px] opacity-60" style={{ letterSpacing: "0.32em" }}>
                                intollerantia.com
                            </div>
                        </div>
                        <div className="paper p-12 md:p-16 min-h-[420px]">
                            <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.4em" }}>
                                MMXXVII — INDEX
                            </div>
                            <div className="mt-8">
                                <Wordmark size="text-5xl md:text-8xl" tracking="tracking-[0.06em]" />
                            </div>
                            <div className="mt-6 h-px w-40" style={{ background: "#C9A96A" }} />
                            <div className="mt-4 font-micro text-xs opacity-80" style={{ letterSpacing: "0.4em" }}>
                                {lang === "en" ? "ITALIAN MAISON — RARE EDITIONS" : "MAISON ITALIANA — EDIZIONI RARE"}
                            </div>
                            <div className="mt-12 grid grid-cols-3 gap-4">
                                <div className="aspect-[3/4] overflow-hidden">
                                    <img src={IMAGES.bottle1} alt="" className="w-full h-full object-cover" />
                                </div>
                                <div className="aspect-[3/4] overflow-hidden">
                                    <img src={IMAGES.olive2} alt="" className="w-full h-full object-cover" />
                                </div>
                                <div className="aspect-[3/4] overflow-hidden">
                                    <img src={IMAGES.salt1} alt="" className="w-full h-full object-cover" />
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- XVII. SOCIAL ---------- */
export function SocialChapter() {
    const { lang } = useLang();
    const c = CONTENT.social[lang];
    const grid = [
        IMAGES.olive1,
        "text:MENO QUANTITÀ · PIÙ VALORE",
        IMAGES.bottle1,
        IMAGES.hands1,
        IMAGES.salt2,
        "text:R",
        IMAGES.marettimo,
        "text:MMXXVII",
        IMAGES.olive2,
    ];
    return (
        <ChapterShell slug="social">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XVII" />
            </div>
            <div className="col-span-12 md:col-span-6 mt-4">
                <Reveal>
                    <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]">{c.body}</p>
                </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8 md:col-start-3 mt-16">
                <Reveal>
                    <div className="grid grid-cols-3 gap-1">
                        {grid.map((g, i) => (
                            <div key={i} className="aspect-square overflow-hidden" style={{ background: g.startsWith("text:") ? (i === 5 ? "#0B0B0B" : "#F4EFE6") : "transparent" }}>
                                {g.startsWith("text:") ? (
                                    <div className="w-full h-full flex items-center justify-center p-4 text-center">
                                        {g.slice(5) === "R" ? (
                                            <span className="font-didone" style={{ transform: "scaleX(-1)", display: "inline-block", fontSize: "6rem", color: "#C9A96A" }}>R</span>
                                        ) : (
                                            <span className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: i === 5 ? "#F4EFE6" : "#0B0B0B" }}>
                                                {g.slice(5)}
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <img src={g} alt="" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(0.35) sepia(0.15)" }} />
                                )}
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- XVIII. RETAIL ---------- */
export function RetailChapter() {
    const { lang } = useLang();
    const c = CONTENT.retail[lang];
    return (
        <ChapterShell slug="retail" dark>
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XVIII" dark />
            </div>
            <div className="col-span-12 md:col-span-7 mt-4">
                <Reveal>
                    <p className="font-serif-lux text-2xl md:text-3xl leading-[1.4]" style={{ color: "#F4EFE6" }}>
                        {c.body}
                    </p>
                </Reveal>
            </div>
            <div className="col-span-12 mt-16 grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                    ["01", "5★ Hotels"],
                    ["02", "Private Clubs"],
                    ["03", "Yachts"],
                    ["04", "Fine Dining"],
                    ["05", "Concept Stores"],
                ].map(([n, label], i) => (
                    <Reveal key={i} delay={i * 0.08}>
                        <div className="border-t pt-5" style={{ borderColor: "rgba(201,169,106,0.35)" }}>
                            <div className="font-didone italic text-sm" style={{ color: "#C9A96A" }}>{n}</div>
                            <div className="mt-3 font-didone text-2xl md:text-3xl" style={{ color: "#F4EFE6" }}>{label}</div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </ChapterShell>
    );
}

/* ---------- XIX. BRAND RULES ---------- */
export function RulesChapter() {
    const { lang } = useLang();
    const c = CONTENT.rules[lang];
    return (
        <ChapterShell slug="rules">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XIX" />
            </div>
            <div className="col-span-12 md:col-span-10 md:col-start-2 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                {c.rules.map((r, i) => (
                    <Reveal key={i} delay={(i % 2) * 0.08}>
                        <div className="flex items-baseline gap-6 py-5 border-b" style={{ borderColor: "rgba(11,11,11,0.15)" }}>
                            <span className="font-didone italic text-lg w-10" style={{ color: "#C9A96A" }}>
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="font-serif-lux text-xl md:text-2xl">{r}</span>
                        </div>
                    </Reveal>
                ))}
            </div>
        </ChapterShell>
    );
}

/* ---------- XX. CLOSING ---------- */
export function ClosingChapter() {
    const { lang } = useLang();
    const c = CONTENT.closing[lang];
    return (
        <ChapterShell slug="closing" dark>
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XX" dark />
            </div>
            <div className="col-span-12 md:col-span-9 md:col-start-2 mt-4">
                <Reveal>
                    <p className="font-serif-lux italic text-3xl md:text-5xl leading-[1.25]" style={{ color: "#F4EFE6" }}>
                        {c.body}
                    </p>
                </Reveal>
                <Reveal delay={0.3}>
                    <div className="mt-16 flex items-center gap-6">
                        <div className="h-px w-24" style={{ background: "#C9A96A" }} />
                        <span className="font-didone italic text-2xl" style={{ color: "#C9A96A" }}>
                            {c.signature}
                        </span>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-12 mt-24 flex justify-center">
                <Reveal>
                    <MonogramSeal size={220} color="#F4EFE6" ring="#C9A96A" />
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- XXI. CONTACT / DISTRIBUTOR FORM ---------- */
export function ContactChapter() {
    const { lang } = useLang();
    const t = UI[lang];
    const c = CONTENT.contact[lang];
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        country: "",
        category: "food",
        message: "",
    });
    const [status, setStatus] = useState("idle"); // idle | sending | ok | err

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) return;
        setStatus("sending");
        try {
            await axios.post(`${API}/distributor-enquiries`, { ...form, lang });
            setStatus("ok");
            setForm({ name: "", email: "", company: "", country: "", category: "food", message: "" });
        } catch (err) {
            console.error(err);
            setStatus("err");
        }
    };

    const inputCls =
        "w-full bg-transparent border-b py-3 px-0 font-serif-lux text-xl focus:outline-none focus:border-b-2 transition-colors";

    return (
        <ChapterShell slug="contact">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XXI" />
            </div>
            <div className="col-span-12 md:col-span-5 mt-4">
                <Reveal>
                    <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]">{c.body}</p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-12 space-y-3 font-micro text-[10px] opacity-80" style={{ letterSpacing: "0.32em" }}>
                        <div>INTOLLERANTIA — MAISON ITALIANA</div>
                        <div>TRAPANI · SICILIA · ITALIA</div>
                        <div>
                            <a href="mailto:intollerantia@gmail.com" className="link-ed">
                                intollerantia@gmail.com
                            </a>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 mt-4">
                <Reveal delay={0.1}>
                    <form onSubmit={submit} data-testid={TID.contactForm} className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                    {t.name}
                                </label>
                                <input data-testid={TID.formName} required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} style={{ borderColor: "rgba(11,11,11,0.35)" }} />
                            </div>
                            <div>
                                <label className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                    {t.email}
                                </label>
                                <input data-testid={TID.formEmail} required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} style={{ borderColor: "rgba(11,11,11,0.35)" }} />
                            </div>
                            <div>
                                <label className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                    {t.company}
                                </label>
                                <input data-testid={TID.formCompany} value={form.company} onChange={(e) => update("company", e.target.value)} className={inputCls} style={{ borderColor: "rgba(11,11,11,0.35)" }} />
                            </div>
                            <div>
                                <label className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                    {t.country}
                                </label>
                                <input data-testid={TID.formCountry} value={form.country} onChange={(e) => update("country", e.target.value)} className={inputCls} style={{ borderColor: "rgba(11,11,11,0.35)" }} />
                            </div>
                        </div>
                        <div>
                            <label className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                {t.category}
                            </label>
                            <select
                                data-testid={TID.formCategory}
                                value={form.category}
                                onChange={(e) => update("category", e.target.value)}
                                className={`${inputCls} appearance-none cursor-pointer`}
                                style={{ borderColor: "rgba(11,11,11,0.35)" }}
                            >
                                <option value="food">{t.catFood}</option>
                                <option value="beauty">{t.catBeauty}</option>
                                <option value="home">{t.catHome}</option>
                            </select>
                        </div>
                        <div>
                            <label className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                {t.message}
                            </label>
                            <textarea data-testid={TID.formMessage} required value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} className={inputCls} style={{ borderColor: "rgba(11,11,11,0.35)", resize: "none" }} />
                        </div>
                        <div className="flex items-center justify-between gap-4 pt-4">
                            <button
                                type="submit"
                                data-testid={TID.formSubmit}
                                disabled={status === "sending"}
                                className="group font-micro text-xs flex items-center gap-4 disabled:opacity-40"
                                style={{ letterSpacing: "0.4em" }}
                            >
                                <span className="link-ed">{status === "sending" ? t.submitting.toUpperCase() : t.submit.toUpperCase()}</span>
                                <span className="inline-block transition-transform group-hover:translate-x-2">→</span>
                            </button>
                            <div data-testid={TID.formStatus} className="font-micro text-[10px] opacity-80" style={{ letterSpacing: "0.32em" }}>
                                {status === "ok" && <span style={{ color: "#3A3E2C" }}>{t.success.toUpperCase()}</span>}
                                {status === "err" && <span style={{ color: "#7a1f1f" }}>{t.failure.toUpperCase()}</span>}
                            </div>
                        </div>
                    </form>
                </Reveal>
            </div>
        </ChapterShell>
    );
}
